import React, { FC, useMemo } from 'react';

import { Box, HStack } from '@chakra-ui/react';
import { some } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { capsSeverityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import {
  alertsCloudProviderOptionsWithAll,
  Select,
  SelectHistoricalOptions,
} from 'components/index';
import { useFindingsIssueContext } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/context';
import { actions } from 'containers/Findings/Alerts/slice';
import { alertStatusOptions } from 'containers/Findings/Alerts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

import { selectAlerts } from '../../selectors';

export const AlertFilters: FC = () => {
  const dispatch = useDispatch();
  const {
    aggregatedFindings,
    alerts,
    entityDetails,
    startDate,
    groupByResource,
  } = useSelector(selectAlerts);
  const {
    aiParamExtraction,
    blueprints,
    alertResourceTypes,
    riskContext,
  } = useSelector(selectSharedState);
  const {
    severity,
    alertStatus,
    alertCategoryOptions,
    alertCategory,
    cloud,
    resourceTypeOptions,
    resourceType,
    blueprintOptions,
    blueprintId,
    updateValue,
  } = useFindingsIssueContext();

  const isLoading = useMemo(() => {
    return (
      alerts.isLoading ||
      aggregatedFindings.isLoading ||
      aiParamExtraction.isLoading ||
      riskContext.isLoading ||
      some(entityDetails, o => o.isLoading)
    );
  }, [
    alerts.isLoading,
    aggregatedFindings.isLoading,
    aiParamExtraction.isLoading,
    riskContext.isLoading,
    entityDetails,
  ]);

  const maxWidthStyles = {
    menu: provided => ({
      ...provided,
      width: 'max-content',
      minWidth: '100%',
    }),
    placeholder: st => ({
      ...st,
      color: customTheme.colors.gray['400'],
    }),
  };

  return (
    <>
      <HStack>
        <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
        <Box>
          <HStack>
            {!groupByResource && (
              <Box>
                <Select
                  options={alertStatusOptions}
                  value={alertStatus}
                  onChange={s => updateValue('alertStatus', s)}
                  showIconInValueContainer
                  isAFilter
                  showClearIndicator={false}
                  isDisabled={isLoading}
                  menuPortalTarget={document.body}
                  leftMessage="Status"
                />
              </Box>
            )}
            <Box>
              <Select
                options={capsSeverityOptions}
                value={severity}
                onChange={s => updateValue('severity', s)}
                showIconInValueContainer
                isMulti
                isAFilter
                showTotalSelected
                styles={maxWidthStyles}
                placeholder="Severity"
                leftMessage="Severity"
                isDisabled={isLoading}
                menuPortalTarget={document.body}
                showUndefinedValueLabel={false}
              />
            </Box>
            <Box>
              <Select
                options={alertCategoryOptions}
                value={alertCategory}
                onChange={s => updateValue('alertCategory', s)}
                leftMessage="Category"
                showIconInValueContainer
                isMulti
                isAFilter
                showTotalSelected
                styles={maxWidthStyles}
                placeholder="Category"
                isDisabled={isLoading}
                menuPortalTarget={document.body}
                showUndefinedValueLabel={false}
              />
            </Box>
            {!groupByResource && (
              <Box>
                <Select
                  options={alertsCloudProviderOptionsWithAll}
                  value={cloud}
                  onChange={s => updateValue('cloud', s)}
                  showIconInValueContainer
                  isAFilter
                  leftMessage="Cloud"
                  menuPortalTarget={document.body}
                  showUndefinedValueLabel={false}
                />
              </Box>
            )}
            <Box>
              <Select
                options={resourceTypeOptions}
                value={resourceType}
                onChange={s => updateValue('resourceType', s)}
                isLoading={alertResourceTypes.isLoading}
                showIconInValueContainer
                menuShouldScrollIntoView
                isMulti
                isAFilter
                showTotalSelected
                styles={maxWidthStyles}
                placeholder="Resource type"
                leftMessage="Resource type"
                isDisabled={isLoading}
                menuPortalTarget={document.body}
                showUndefinedValueLabel={false}
              />
            </Box>
            {!groupByResource && (
              <Box>
                <Select
                  options={blueprintOptions}
                  value={blueprintId}
                  onChange={s => {
                    updateValue('blueprintId', s);
                  }}
                  isLoading={blueprints.isLoading}
                  showIconInValueContainer
                  isAFilter
                  styles={maxWidthStyles}
                  isDisabled={isLoading}
                  leftMessage="Blueprint"
                  menuPortalTarget={document.body}
                  showUndefinedValueLabel={false}
                />
              </Box>
            )}
            <SelectHistoricalOptions
              historicalTime={startDate}
              setHistoricalTime={s => {
                dispatch(actions.updateStartDate(s));
              }}
              isDisabled={isLoading}
              width="inherit"
              leftMessage="Timeline"
              isAFilter
            />
          </HStack>
        </Box>
      </HStack>
    </>
  );
};
